import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import type { User } from "@auth0/auth0-react";

import { api } from "@/services/api";
import { kaiApi } from "@/services/kaiApi";
import { isAdminRole } from "@/lib/userDisplay";
import type { UserProfile } from "@/types/api";
import {
  auth0UserOrganizationId,
  persistOrganizationLoginChoice,
  rememberAuth0Organization,
} from "./authOrganization";
import { authReturnTo, nativeOpenUrl } from "./nativeAuth";
import { signalAuthLogout } from "./sessionSync";

const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

interface UserContextValue {
  /** Auth0 identity (name, email, picture). */
  user: User | undefined;
  /** Local DB profile from /v1/users/me (role, org, client_key). */
  profile: UserProfile | null;
  isAuthenticated: boolean;
  /** Auth0 still resolving the session (initial load / silent auth). */
  isAuthLoading: boolean;
  /** Profile fetch in flight. */
  isProfileLoading: boolean;
  /** Set when `/v1/users/me` fails after authentication. */
  profileError: string | null;
  /** `true` when the local profile role is Admin or SystemAdmin. */
  isAdmin: boolean;
  /** `true` when the local profile role is SystemAdmin. */
  isSystemAdmin: boolean;
  /** `true` when the local profile role is SalesPerson. */
  isSalesPerson: boolean;
  /** `true` when the local profile role is Client (consignee portal). */
  isClient: boolean;
  /** `true` when the local profile role is Finance. */
  isFinance: boolean;
  /** Opportunity list + savings calculation (`read:rebooks`, never SalesPerson/Client). */
  canViewSavings: boolean;
  /** Accept / dismiss a rebook recommendation (`create:rebooks`). */
  canCreateRebooks: boolean;
  /** Create / revise rate cards (`create:rates`, never SalesPerson). */
  canCreateRates: boolean;
  /** RBAC permissions from `/v1/users/me`. */
  permissions: string[];
  getAccessToken: () => Promise<string>;
  refreshProfile: () => Promise<void>;
  logout: () => void;
  /** Re-login through Auth0 into a different organization. */
  switchOrganization: (auth0OrgId: string) => void;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const {
    user,
    isAuthenticated,
    isLoading: isAuthLoading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const getAccessToken = useCallback(async () => {
    const organization = rememberAuth0Organization(
      auth0UserOrganizationId(user),
    );
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience,
          ...(organization ? { organization } : {}),
        },
      });
    } catch (err) {
      // Silent auth failed — e.g. a stale session from an audience change, a
      // missing refresh token, or a consent requirement. Re-establish the
      // session with the correct audience rather than firing a token-less
      // request (which the API rejects with "Missing Authorization header").
      console.warn("Silent token acquisition failed; redirecting to login", err);
      await loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
        authorizationParams: {
          audience,
          ...(organization ? { organization } : {}),
        },
        openUrl: nativeOpenUrl(),
      });
      throw err;
    }
  }, [getAccessTokenSilently, loginWithRedirect, user]);

  // Wire the token getter into both API clients.
  useEffect(() => {
    api.setTokenGetter(getAccessToken);
    kaiApi.setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsProfileLoading(true);
    setProfileError(null);
    try {
      setProfile(await api.getMe());
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load your profile";
      console.warn("Failed to fetch user profile from /v1/users/me", err);
      setProfile(null);
      setProfileError(message);
    } finally {
      setIsProfileLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !isAuthLoading) {
      void refreshProfile();
      return;
    }
    if (!isAuthenticated && !isAuthLoading) {
      setProfile(null);
      setProfileError(null);
    }
  }, [isAuthenticated, isAuthLoading, refreshProfile]);

  useEffect(() => {
    const orgId = auth0UserOrganizationId(user);
    if (orgId) rememberAuth0Organization(orgId);
  }, [user]);

  const logout = useCallback(() => {
    rememberAuth0Organization(auth0UserOrganizationId(user));
    signalAuthLogout();
    auth0Logout({
      logoutParams: { returnTo: authReturnTo() },
      openUrl: nativeOpenUrl(),
    });
  }, [auth0Logout, user]);

  const switchOrganization = useCallback(
    (auth0OrgId: string) => {
      persistOrganizationLoginChoice(auth0OrgId);
      rememberAuth0Organization(auth0OrgId);
      void loginWithRedirect({
        authorizationParams: {
          audience,
          organization: auth0OrgId,
        },
        openUrl: nativeOpenUrl(),
      });
    },
    [loginWithRedirect],
  );

  const isAdmin = isAdminRole(profile?.role);
  const isSystemAdmin = profile?.role === "SystemAdmin";
  const isSalesPerson = profile?.role === "SalesPerson";
  const isClient = profile?.role === "Client";
  const isFinance = profile?.role === "Finance";
  const permissions = profile?.permissions ?? [];
  const canViewSavings =
    !isSalesPerson && !isClient && permissions.includes("read:rebooks");
  const canCreateRebooks = !isClient && permissions.includes("create:rebooks");
  const canCreateRates =
    !isSalesPerson && !isClient && permissions.includes("create:rates");

  const value: UserContextValue = {
    user,
    profile,
    isAuthenticated,
    isAuthLoading,
    isProfileLoading,
    profileError,
    isAdmin,
    isSystemAdmin,
    isSalesPerson,
    isClient,
    isFinance,
    canViewSavings,
    canCreateRebooks,
    canCreateRates,
    permissions,
    getAccessToken,
    refreshProfile,
    logout,
    switchOrganization,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within UserProvider");
  }
  return ctx;
}

/** Non-throwing variant for components that may render outside the provider. */
export function useUserOptional() {
  return useContext(UserContext);
}
